import {
  format,
  getISOWeek,
  getMonth,
  getYear,
  isFirstDayOfMonth,
  isLastDayOfMonth,
  isSameDay,
  isSameMonth,
  isSameYear,
  subDays,
  subMonths,
  subWeeks,
} from "date-fns";
import { is, map, merge, objOf, reject } from "ramda";

import type { IDateRange } from "types/dates";

// Saturday, Jun 2nd 2018
export const DATE_WITH_DAY_OF_WEEK = "dddd, MMM Do YYYY";

export enum QueryParamDateFormat {
  BREADCRUMB = "BREADCRUMB",
  UPSERVE = "UPSERVE",
  UPSERVE_MONTH_ONLY = "UPSERVE_MONTH_ONLY",
}

export type DateRange = RelativeDateRange | AbsoluteDateRange;

export enum DateRangeSupport {
  FULL, // Includes all relative and custom options
  RELATIVE_ONLY, // Includes relative only options
  NONE, // No support for date ranges
  FUTURE_BY_WEEK, // Allow for future dates, organized by week
}

export enum RelativeDateRange {
  YEAR,
  TODAY,
  YESTERDAY,
  LAST_7_DAYS,
  LAST_MONTH,
  LAST_12_WEEKS,
  LAST_3_MONTHS,
}

export enum AbsoluteDateRange {
  CUSTOM = "CUSTOM",
  YEAR = "YEAR",
  MONTH = "MONTH",
  WEEK = "WEEK",
}
// Turn enum into array
export function enumToArray(enumValue: any) {
  return map(
    (key: string) => enumValue[key],
    reject(is(Number), Object.keys(enumValue))
  );
}

export const RelativeDateRangesName = new Map<number, string>([
  [RelativeDateRange.YEAR, "Year"],
  [RelativeDateRange.TODAY, "Today"],
  [RelativeDateRange.YESTERDAY, "Yesterday"],
  [RelativeDateRange.LAST_7_DAYS, "Last 7 days"],
  [RelativeDateRange.LAST_MONTH, "Last Month"],
  [RelativeDateRange.LAST_12_WEEKS, "Last 12 Weeks"],
  [RelativeDateRange.LAST_3_MONTHS, "Last 3 Months"],
]);

export function datesForRelativeRange(relativeRange: DateRange): IDateRange {
  const currentDay = new Date();
  const currentEndDate = (presentDay: Date = currentDay) =>
    merge(objOf("end", presentDay), objOf("rangeKey", relativeRange));
  switch (relativeRange) {
    case RelativeDateRange.YESTERDAY:
      const yesterday = subDays(currentDay, 1);
      return merge({ start: yesterday }, currentEndDate(yesterday));
    case RelativeDateRange.LAST_7_DAYS:
      return merge({ start: subDays(currentDay, 7) }, currentEndDate());
    case RelativeDateRange.LAST_MONTH:
      return merge({ start: subMonths(currentDay, 1) }, currentEndDate());
    case RelativeDateRange.LAST_12_WEEKS:
      return merge({ start: subWeeks(currentDay, 12) }, currentEndDate());
    case RelativeDateRange.LAST_3_MONTHS:
      return merge({ start: subMonths(currentDay, 3) }, currentEndDate());
    default:
      return merge({ start: currentDay }, currentEndDate());
  }
}

function formatForQueryParams(
  date: Date,
  dateFormat: QueryParamDateFormat
): string {
  if (!date) {
    return "";
  }

  switch (dateFormat) {
    case QueryParamDateFormat.BREADCRUMB:
      return format(date, "MM-DD-YYYY");
    case QueryParamDateFormat.UPSERVE:
      return format(date, "YYYY-MM-DD");
    case QueryParamDateFormat.UPSERVE_MONTH_ONLY:
      return format(date, "YYYY-MM");
    default:
      return "";
  }
}

function toWeekDateRange(
  startDate: Date,
  endDate: Date,
  showYear: boolean = true,
  today: Date = new Date()
): string {
  const currentISOWeek = getISOWeek(today);
  const startDateISOWeek = getISOWeek(startDate);
  const todaysYear = getYear(today);
  const startDateYear = getYear(startDate);
  const isThisWeek =
    currentISOWeek === startDateISOWeek && isSameYear(startDate, today);
  const isLastWeek =
    (currentISOWeek - startDateISOWeek === 1 && isSameYear(startDate, today)) ||
    (currentISOWeek - startDateISOWeek === -51 &&
      todaysYear - startDateYear === 1);

  let label;
  if (isThisWeek) {
    label = "This Week";
  } else if (isLastWeek) {
    // wrapping for new year
    label = "Last Week";
  } else {
    label = `${format(startDate, "MMM D")} - `;
    if (getMonth(startDate) !== getMonth(endDate)) {
      label += format(endDate, "MMM D");
    } else {
      label += format(endDate, "D");
    }
    if (showYear && getYear(today) !== getYear(endDate)) {
      label += format(endDate, ", YYYY");
    }
  }

  return label;
}

function dateRangeName(startDate: Date | null, endDate: Date | null): string {
  if (!startDate) {
    return "";
  }
  if (!endDate) {
    return format(startDate, "MMMM D, YYYY");
  }

  let formattedStart;
  let formattedEnd;

  if (isSameYear(startDate, endDate)) {
    if (isSameMonth(startDate, endDate)) {
      if (isSameDay(startDate, endDate)) {
        return format(startDate, "MMMM D, YYYY");
      }
      if (isFirstDayOfMonth(startDate) && isLastDayOfMonth(endDate)) {
        // December 2012
        return format(startDate, "MMMM YYYY");
      }

      // December 21 - 31, 2012
      formattedStart = format(startDate, "MMMM D");
      formattedEnd = format(endDate, "D, YYYY");
    } else {
      // Nov 5 - Dec 3, 2013
      formattedStart = format(startDate, "MMM D");
      formattedEnd = format(endDate, "MMM D, YYYY");
    }
  } else {
    // Dec 24, 2012 - Jan 3, 2013
    formattedStart = format(startDate, "MMM D, YYYY");
    formattedEnd = format(endDate, "MMM D, YYYY");
  }

  return `${formattedStart} - ${formattedEnd}`;
}

export { toWeekDateRange, formatForQueryParams, dateRangeName };
